/**
 * What the bid form sends, what it refuses before sending, and what the server's refusals mean —
 * `W6-T06`.
 *
 * The two rules a provider can break are checked here first, with the numbers they need — the
 * client's ceiling, and their own lowest bid (lower only, operator 2026-10-01). The server checks
 * both again and is the authority; this is what lets the screen say *which* rule, rather than a
 * generic refusal. Keys, never sentences.
 */
import { type Bid, type BidInput } from '@marketplace/contracts';
import { ApiError } from '../../shared/api-error.js';
import { eurosToCents } from '../jobs/posting.js';

export type BidFormField = 'amount' | 'message';

export type BidFormResult =
  { ok: true; input: BidInput } | { ok: false; errors: Partial<Record<BidFormField, string>> };

/** Lower only is measured against **every** earlier bid, withdrawn and replaced included. */
export function lowestOf(bids: readonly Bid[]): number | null {
  return bids.reduce<number | null>(
    (min, bid) => (min === null || bid.amountCents < min ? bid.amountCents : min),
    null,
  );
}

function text(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === 'string' ? value.trim() : '';
}

export function parseBidForm(
  form: FormData,
  limits: { lowestCents: number | null; maxPriceCents: number | null },
): BidFormResult {
  const errors: Partial<Record<BidFormField, string>> = {};

  const typed = text(form, 'amount');
  const amountCents = typed === '' ? undefined : eurosToCents(typed);
  if (typed === '') errors.amount = 'bidForm.error.amount';
  else if (amountCents === undefined || amountCents === 0)
    errors.amount = 'bidForm.error.amountFormat';
  else if (limits.maxPriceCents !== null && amountCents > limits.maxPriceCents) {
    errors.amount = 'bidForm.error.aboveMax';
  } else if (limits.lowestCents !== null && amountCents >= limits.lowestCents) {
    errors.amount = 'bidForm.error.notLower';
  }

  const message = text(form, 'message');
  if (message.length > 2000) errors.message = 'bidForm.error.messageLong';

  if (amountCents === undefined || Object.keys(errors).length > 0) return { ok: false, errors };
  return { ok: true, input: { amountCents, ...(message === '' ? {} : { message }) } };
}

/** A refusal as the sentence the provider reads — keyed on the status, never the English text. */
export function bidFailureKey(error: unknown): string {
  if (!(error instanceof ApiError)) return 'bidForm.error.failed';
  if (error.status === 404) return 'bidForm.error.gone';
  if (error.status === 403) return 'bidForm.error.own';
  // Closed, above the ceiling, or not lower — the checks above catch the last two first, so a 409
  // that still arrives is almost always the clock.
  if (error.status === 409) return 'bidForm.error.conflict';
  return 'bidForm.error.failed';
}
